import { apiClient } from '../api-client';
import { AGENTS } from '../../api-endpoints/api-endpoints';
import { ApiAgent, CreateAgentInput, ListAgentsResponse, AgentAvailability } from '../../schema/agents/agent.schema';

export const agentsService = {
  list: (filters?: { group?: string; availability?: AgentAvailability; isActive?: boolean }): Promise<ListAgentsResponse> => {
    const q = filters ? '?' + Object.entries(filters).filter(([, v]) => v !== undefined).map(([k, v]) => `${k}=${v}`).join('&') : '';
    return apiClient.get<ListAgentsResponse>(AGENTS.list + q);
  },

  get: (id: string): Promise<ApiAgent> =>
    apiClient.get<ApiAgent>(AGENTS.get(id)),

  create: (body: CreateAgentInput): Promise<ApiAgent> =>
    apiClient.post<ApiAgent>(AGENTS.create, body),

  /**
   * Idempotently register the signed-in user as a ticketing agent so they
   * become assignable. The tickets API requires the assignee to be a
   * registered active agent, but workspace admins/owners are platform members
   * who were never explicitly "added" as agents — without this they cannot
   * even self-assign. A 409 (already registered) is treated as success.
   */
  ensureSelf: async (user: { id: string; first_name?: string; last_name?: string; email?: string }): Promise<ApiAgent | null> => {
    if (!user?.id) return null;
    const displayName =
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.email || 'Agent';
    try {
      return await apiClient.post<ApiAgent>(AGENTS.create, {
        platformUserId: user.id,
        displayName,
        email: user.email,
        availability: 'online',
      });
    } catch (e: any) {
      if (e?.response?.status === 409) return null; // already an agent — fine
      throw e;
    }
  },

  update: (id: string, body: Partial<CreateAgentInput>): Promise<ApiAgent> =>
    apiClient.patch<ApiAgent>(AGENTS.update(id), body),

  delete: (id: string): Promise<void> =>
    apiClient.delete<void>(AGENTS.delete(id)),

  setAvailability: (availability: AgentAvailability): Promise<void> =>
    apiClient.put<void>(AGENTS.myAvailability, { availability }),
};
